import { Link } from "react-router";

import {
  isEventSubscription,
  ManagedByKind,
  PlatformEventType,
  type TriggerRecord,
} from "@/shared/domain/triggers";
import { useAllTriggers } from "@/shared/domain/triggers/hooks";
import { Goto } from "@/shared/navigation";

import styles from "./SessionIngestedTriggers.module.css";

/** The name Roboto gives the session-metrics trigger it installs. */
const SESSION_METRICS_TRIGGER_NAME = "roboto-session-metrics";

const SESSION_METRICS_DESCRIPTION =
  " — Roboto's own trigger. It computes session metrics.";

function startsOnSessionIngested(trigger: TriggerRecord): boolean {
  return (
    trigger.enabled &&
    isEventSubscription(trigger.fires_on) &&
    trigger.fires_on.events.includes(PlatformEventType.SessionIngested)
  );
}

function isSessionMetricsTrigger(trigger: TriggerRecord): boolean {
  return (
    trigger.managed_by?.kind === ManagedByKind.System &&
    trigger.managed_by.id === "session_metrics"
  );
}

/**
 * The enabled triggers that start when a session is fully ingested, after
 * ``lead``. ``installsMetrics`` names who will install Roboto's
 * session-metrics trigger when the org does not have it yet (a completion
 * installs it; pass null where nothing will).
 */
export function SessionIngestedTriggers({
  orgId,
  lead,
  installsMetrics,
}: {
  orgId: string;
  lead: string;
  installsMetrics: string | null;
}) {
  const triggers = useAllTriggers(orgId);

  if (triggers.isPending) {
    return <p className={styles.lead}>{lead}.</p>;
  }
  if (triggers.isError) {
    return (
      <p className={styles.lead}>
        {lead}. Roboto could not load the triggers that start then.
      </p>
    );
  }

  const starting = triggers.data.filter(startsOnSessionIngested);
  const addsMetrics =
    installsMetrics !== null && !triggers.data.some(isSessionMetricsTrigger);
  if (starting.length === 0 && !addsMetrics) {
    return (
      <p className={styles.lead}>
        {lead}. No triggers are set to start when a session is fully ingested.
      </p>
    );
  }
  return (
    <>
      <p className={styles.lead}>{lead}, and these triggers start:</p>
      <ul className={styles.triggers} data-testid="session-ingested-triggers">
        {starting.map((trigger) => (
          <li key={trigger.trigger_id}>
            <Link
              to={Goto.createTriggerRoute(trigger.trigger_id, trigger.name)}
            >
              {trigger.name}
            </Link>
            {isSessionMetricsTrigger(trigger) && SESSION_METRICS_DESCRIPTION}
            {trigger.condition !== null &&
              (isSessionMetricsTrigger(trigger)
                ? " It starts only if its condition matches."
                : " — starts only if its condition matches.")}
          </li>
        ))}
        {addsMetrics && (
          <li>
            {SESSION_METRICS_TRIGGER_NAME}
            {SESSION_METRICS_DESCRIPTION} Roboto adds it to this organization{" "}
            {installsMetrics}.
          </li>
        )}
      </ul>
    </>
  );
}
